/**
 * RespondHero widget entry point.
 *
 * Customer sites embed the bundle via:
 *
 *   <script
 *     src="https://widget.respondhero.com/v1/widget.js"
 *     data-tenant="bazaaronline"
 *     data-key="pub_..."
 *     data-locale="nl-NL"
 *     async></script>
 *
 * This file:
 *   1. Reads its own <script> tag's `data-*` attributes.
 *   2. Creates an isolated container at the bottom of <body>.
 *   3. Attaches a Shadow DOM so customer CSS cannot bleed in.
 *   4. Mounts the Preact app inside the shadow root.
 *
 * If the script tag is malformed (no tenant or no key), we log a
 * single console error and exit silently. We never throw on a
 * customer's site.
 */

import { render } from "preact";
import { WidgetApp } from "./app";
import { WIDGET_CSS } from "./styles";
import type { SupportedLocale, WidgetBootConfig } from "./types";

const DEFAULT_API_BASE_URL = "https://api.respondhero.com";

/**
 * Global injection helper used by the public demo page. Strictly
 * scoped: only attached when `data-open-by-default="true"`, so
 * customer sites never see this property added to their `window`.
 */
declare global {
  interface Window {
    __rh_send__?: (text: string) => void;
  }
}

function readBootConfig(): WidgetBootConfig | null {
  // `document.currentScript` is null in modules and after async load
  // races, so fall back to scanning for a script tag with our data
  // attributes. The bundle is always loaded as a classic IIFE.
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-tenant][data-key]");

  if (!script) return null;

  const tenant = script.getAttribute("data-tenant")?.trim() ?? "";
  const key = script.getAttribute("data-key")?.trim() ?? "";
  const locale = script.getAttribute("data-locale")?.trim() || null;
  const apiBaseUrl = script.getAttribute("data-api-base-url")?.trim() || DEFAULT_API_BASE_URL;
  const openByDefault = script.getAttribute("data-open-by-default")?.trim() === "true";

  if (!tenant || !key) return null;

  return {
    tenant,
    key,
    locale: (locale as SupportedLocale | null) ?? null,
    apiBaseUrl,
    openByDefault,
  };
}

function mountWidget(boot: WidgetBootConfig): void {
  // Container is positioned `fixed` via the launcher/panel classes,
  // so it does not shift the customer's layout regardless of where
  // the host element sits in the DOM.
  const host = document.createElement("div");
  host.id = "respondhero-widget-host";
  host.style.cssText = "all: initial; position: fixed; z-index: 2147483000;";
  document.body.appendChild(host);

  // closed mode: customer JS cannot reach into our shadow root.
  // Tradeoff: we cannot debug-inspect via `host.shadowRoot` either.
  // For a customer-facing widget the isolation matters more.
  const shadow = host.attachShadow({ mode: "closed" });

  // Inter is declared via `@font-face` inside WIDGET_CSS pointing at
  // self-hosted woff2 files next to widget.js on Cloudflare Pages.
  // The browser fetches the font lazily on first render, so no
  // separate preload step is needed here. See styles.ts for the
  // rationale on self-hosting.
  const styleEl = document.createElement("style");
  styleEl.textContent = WIDGET_CSS;
  shadow.appendChild(styleEl);

  const mountPoint = document.createElement("div");
  shadow.appendChild(mountPoint);

  // Demo bridge: when the panel is open by default we expose a tiny
  // helper so the marketing /demo page can pre-fill suggested prompts.
  // Gated on the flag to avoid polluting customer sites with globals.
  const registerSendFn = boot.openByDefault
    ? (fn: (text: string) => void) => {
        window.__rh_send__ = fn;
      }
    : undefined;

  render(<WidgetApp boot={boot} registerSendFn={registerSendFn} />, mountPoint);
}

function boot(): void {
  if (typeof document === "undefined") return;

  // Mark the container so the widget cannot mount twice if someone
  // accidentally embeds the script twice on the same page.
  if (document.getElementById("respondhero-widget-host")) return;

  const config = readBootConfig();
  if (!config) {
    // eslint-disable-next-line no-console -- intentional one-shot dev hint
    console.error("RespondHero widget: missing data-tenant or data-key on the script tag.");
    return;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mountWidget(config), {
      once: true,
    });
  } else {
    mountWidget(config);
  }
}

boot();
